import {
  useLazyGetInterviewStep0Query,
  useLazyGetInterviewStep1Query,
  useVerifyInterviewTokenMutation,
} from "@/features/interview/interviewApi";
import {
  updateFlowState,
  selectInterviewStep,
  selectFlowData,
} from "@/features/interviewFlow";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { FallbackRole, InterviewFallbackVariant } from "@/components/fallbacks/types";

export type ErrorType = InterviewFallbackVariant | null;
export type UserRole = FallbackRole | null;

type ApiError = {
  status?: number;
  data?: {
    statusCode?: number;
    message?: string;
    cause?: string;
    type?: string;
  };
  originalStatus?: number;
};

export function useInterviewFlow() {
  const dispatch = useAppDispatch();
  const step = useAppSelector(selectInterviewStep);
  const flowData = useAppSelector(selectFlowData);

  const [verifyInterviewToken] = useVerifyInterviewTokenMutation();
  const [getStep0] = useLazyGetInterviewStep0Query();
  const [getStep1] = useLazyGetInterviewStep1Query();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorType>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [message, setMessage] = useState<string | null>(null);

  const hasRun = useRef(false);
  const { token } = useParams();

  useEffect(() => {
    if (!token) {
      setError("invalid");
      setLoading(false);
      return;
    }

    if (hasRun.current) return;
    hasRun.current = true;

    const validateCandidateStep = async () => {
      try {
        const validateRes = await verifyInterviewToken({ token }).unwrap();
        dispatch(updateFlowState({ validationData: validateRes.data }));

        const isPanel = validateRes.data.role === "panel";
        setRole(isPanel ? "panel" : "candidate");

        if (isPanel) {
          const res = await getStep0().unwrap();
          dispatch(updateFlowState({ flowData: { step: 0, data: res.data } }));
        } else {
          const currentStep = validateRes.data.step;

          if (currentStep === 0) {
            const res = await getStep0().unwrap();
            dispatch(updateFlowState({ flowData: { step: 0, data: res.data } }));
          } else if (currentStep === 1) {
            const res = await getStep1().unwrap();
            dispatch(updateFlowState({ flowData: { step: 1, data: res.data } }));
          } else if (currentStep >= 2) {
            setError("rejoin");
          }
        }
      } catch (err) {
        const apiErr = err as ApiError;
        const status =
          apiErr?.status ?? apiErr?.data?.statusCode ?? apiErr?.originalStatus;
        const cause = apiErr?.data?.cause;
        const type = apiErr?.data?.type;
        const apiMsg = apiErr?.data?.message;

        if (status === 404) setError("invalid");
        else if (cause === "early_join") setError("too-early");
        else if (cause === "expired") setError("expired");
        else if (cause === "completed") setError("completed");
        else if (cause === "abandoned") setError("abandoned");
        else if (cause === "cancelled") setError("cancelled");
        else setError("error");

        if (type === "panel") setRole("panel");
        else if (type === "candidate") setRole("candidate");

        if (apiMsg) setMessage(apiMsg);
      } finally {
        setLoading(false);
      }
    };

    validateCandidateStep();
  }, [token, verifyInterviewToken, getStep0, getStep1, dispatch]);

  return { data: flowData, step, loading, error, role, message };
}
